import Datasource, { DatasourceActionType, DatasourceResponse } from "lib/datasource";
import _ from "lodash";
import moment from "moment";
import React, { memo, useCallback, useEffect, useState } from "react";
import { View } from "components/Shared";
import ReviewActionPanel from "./ReviewActionPanel";
import SubmissionRoundHistory from "./SubmissionRoundHistory";
import SubmissionRoundsSummaryStrip from "./SubmissionRoundsSummaryStrip";

interface Props {
  name: string;
  taskId: string;
  globalId: string;
  canLogOutcome?: boolean;
  onUpdate?: () => void;
  task?: any;
}

const ALL_SENTINEL = { id: "All", name: "All" };

const SubmissionsTab = ({
  taskId,
  canLogOutcome = false,
  onUpdate,
  task = {},
}: Props) => {
  const [rounds, setRounds] = useState<any[]>([]);
  const [localTask, setLocalTask] = useState<any>(task);
  const [taggableUsers, setTaggableUsers] = useState<{ id: string; name: string }[]>([ALL_SENTINEL]);
  // Submission condition resource (id + link) for the RDD upload widget.
  const [submissionCondition, setSubmissionCondition] = useState<{ id: string; link: string } | null>(null);
  const [, renderTrigger] = useState(moment());
  const [datasource] = useState(
    new Datasource({
      mainModelName: "submission_round",
      renderTrigger: renderTrigger,
    })
  );

  // Separate datasource for task-user fetches. Using mainModelName "task" mirrors
  // CommentTab.tsx so the subscription (which checks mainModelName) doesn't
  // interfere with the submission_round datasource subscription below.
  const [, taskRenderTrigger] = useState(moment());
  const [taskDatasource] = useState(
    new Datasource({
      mainModelName: "task",
      renderTrigger: taskRenderTrigger,
    })
  );

  // Separate datasource for fetching the task's conditions to locate the submission condition.
  const [, conditionRenderTrigger] = useState(moment());
  const [conditionDatasource] = useState(
    new Datasource({
      mainModelName: "condition",
      renderTrigger: conditionRenderTrigger,
    })
  );

  // Sync task prop into localTask when parent updates it
  useEffect(() => {
    if (!_.isEmpty(task)) setLocalTask(task);
  }, [task]);

  // Populate taggable users: prefer all_users already on the task/localTask prop
  // (avoids a redundant network call), otherwise fetch via the task datasource.
  useEffect(() => {
    const users: any[] | undefined = _.get(localTask, "all_users");
    if (Array.isArray(users) && users.length > 0) {
      setTaggableUsers([ALL_SENTINEL, ...users]);
    }
  }, [localTask]);

  useEffect(() => {
    const sub = taskDatasource.responseSubject$.subscribe(
      (response: DatasourceResponse) => {
        if (response.success && response.actionType === DatasourceActionType.GET) {
          const users = _.get(response.normalizedMainModelResponse, "all_users", []);
          setTaggableUsers([ALL_SENTINEL, ...(users ?? [])]);
        }
      }
    );
    return () => sub.unsubscribe();
  }, [taskDatasource]);

  useEffect(() => {
    // Only fetch if localTask doesn't already carry all_users.
    const existingUsers: any[] | undefined = _.get(localTask, "all_users");
    if (!taskId || (Array.isArray(existingUsers) && existingUsers.length > 0)) return;
    taskDatasource.get("v2", `tasks/${taskId}`, {
      queryStringParameters: { fields: JSON.stringify({ all_users: ["id", "name"] }) },
    });
  }, [taskId, taskDatasource, localTask]);

  // Fetch the task's conditions to find the submission condition (kind == "submission").
  // The submission condition id + global_id is needed by the RDD upload widget.
  useEffect(() => {
    const sub = conditionDatasource.responseSubject$.subscribe(
      (response: DatasourceResponse) => {
        if (response.success && response.actionType === DatasourceActionType.GET) {
          const conditions: any[] = Array.isArray(response.normalizedMainModelResponse)
            ? response.normalizedMainModelResponse
            : [];
          const subCond = conditions.find((c: any) => c.kind === "submission" && !c.deleted_at);
          if (subCond) {
            setSubmissionCondition({ id: subCond.id, link: subCond.global_id });
          }
        }
      }
    );
    return () => sub.unsubscribe();
  }, [conditionDatasource]);

  useEffect(() => {
    if (!taskId) return;
    conditionDatasource.get("v2", `tasks/${taskId}/conditions`, {
      queryStringParameters: {
        per_page: "-1",
        fields: JSON.stringify({ condition: ["id", "kind", "global_id", "deleted_at"] }),
      },
    });
  }, [taskId, conditionDatasource]);

  const fetchRounds = useCallback(() => {
    if (!taskId) return;
    datasource.get("v2", `tasks/${taskId}/submission_rounds`, {
      queryStringParameters: {
        per_page: "-1",
        sort: "round_number",
        include: JSON.stringify([
          "submitted_by",
          "reviewed_by",
          "reason_code",
          "documents",
          "responsible_documents",
          "rdd_documents",
        ]),
        fields: JSON.stringify({
          submission_round: [
            "round_number",
            "is_current",
            "state",
            "outcome",
            "submitted_at",
            "reviewed_at",
            "notes",
            "document_ref",
            "document_name",
            "review_time_days",
            "submitted_by",
            "reviewed_by",
            "reason_code",
            "documents",
            "responsible_documents",
            "rdd_documents",
          ],
          user: ["id", "name"],
          document: ["id", "name", "display_name", "mimetype", "created_at", "created_by"],
          reason_code: ["id", "label", "is_controlled"],
        }),
      },
    });
  }, [datasource, taskId]);

  useEffect(() => {
    const sub = datasource.responseSubject$.subscribe(
      (response: DatasourceResponse) => {
        if (response.success && response.actionType === DatasourceActionType.GET) {
          const data = response.normalizedMainModelResponse;
          setRounds(Array.isArray(data) ? data : []);
        }
      }
    );
    return () => sub.unsubscribe();
  }, [datasource]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds]);

  const handleOutcomeSuccess = useCallback(
    (updatedTask: any) => {
      if (updatedTask) setLocalTask(updatedTask);
      // Refetch rounds after a successful outcome log
      fetchRounds();
      if (onUpdate) onUpdate();
    },
    [fetchRounds, onUpdate]
  );

  const currentRound =
    rounds.length > 0 ? rounds[rounds.length - 1] : null;

  return (
    <View hideTitle={true} resource="submissionRounds">
      <div className="p-4">
        <SubmissionRoundsSummaryStrip task={localTask} rounds={rounds} />
        <SubmissionRoundHistory rounds={rounds} />
        <ReviewActionPanel
          taskId={taskId}
          currentRound={currentRound}
          canLogOutcome={canLogOutcome}
          onSuccess={handleOutcomeSuccess}
          taggableUsers={taggableUsers}
          submissionCondition={submissionCondition}
          onUploaded={fetchRounds}
        />
      </div>
    </View>
  );
};

export default memo(SubmissionsTab);
